import React, {
  ReactElement,
  KeyboardEventHandler,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import classnames from "classnames";
import { ToolbarItemProps } from "./toolbar-item.types";
import { IconStyle } from "../../types";
import { ToolbarContext } from "../utils/toolbarContext";
import { ToolbarActionType } from "../utils/toolbarReducer.types";
import Tooltip from "../../tooltip";
import { renderIconClassName } from "../../utils";

export const ToolbarItem = ({
  children,
  className,
  label,
  ariaLabel,
  icon,
  iconStyle,
  order,
  priority,
  tooltip,
  closeOnClick = true,
  disabled,
  _privateProps = {},
  onClick,
  ...rest
}: ToolbarItemProps): ReactElement | null => {
  const toolbarItemRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const [animateDropdown, setAnimateDropdown] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const { state, dispatch } = useContext(ToolbarContext);

  const { textButton, visible } = _privateProps;

  const toggleDropdown = (open: boolean): void => {
    if (open === true) {
      setOpenDropdown(true);
      window.setTimeout(() => setAnimateDropdown(true), 0);
    } else {
      setAnimateDropdown(false);
      timeoutRef.current = window.setTimeout(() => setOpenDropdown(false), 100);
    }
  };

  // Clearing timeout on unmount. Needs a separate useEffect with an empty deps array.
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Closing toolbar item dropdowns from toolbar level, if needed
  useEffect(() => {
    const isFloatingUIPortalElement = document.querySelector(
      "[data-floating-ui-portal]"
    );
    // don't close dropdowns if floating UI portal is open (opened from within ToolbarItem)
    if (!isFloatingUIPortalElement) {
      if (state.closeAllToolbarItemDropdowns) {
        toggleDropdown(false);
        dispatch({
          type: ToolbarActionType.CLOSE_ALL_TOOLBAR_ITEM_DROPDOWNS,
          inProgress: false,
        });
      }
    }
  }, [dispatch, state.closeAllToolbarItemDropdowns]);

  const _onWindowClick = useCallback(
    (e: MouseEvent): void => {
      if (animateDropdown) {
        const toolbarItemContent = toolbarItemRef.current?.querySelector(
          ".spark-toolbar__item--content"
        );
        const isFloatingUIPortalElement = document.querySelector(
          "[data-floating-ui-portal]"
        );
        // don't close dropdowns if floating UI portal is open (opened from within ToolbarItem)
        if (!isFloatingUIPortalElement) {
          if (!toolbarItemContent?.contains(e.target as HTMLElement)) {
            toggleDropdown(false);
          }
        }
      }
    },
    [animateDropdown]
  );

  useEffect(() => {
    window.addEventListener("click", _onWindowClick);
    return () => {
      window.removeEventListener("click", _onWindowClick);
    };
  }, [_onWindowClick]);

  const onClickHandler = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ): void => {
    // Making sure we are focusing toolbar item, to prevent `onClick` action on children's dropdown items
    const toolbarItemContent = toolbarItemRef.current?.querySelector(
      ".spark-toolbar__item--content"
    );

    if (
      toolbarItemRef.current === document.activeElement &&
      !toolbarItemContent?.contains(event.target as HTMLElement)
    ) {
      onClick?.(event);

      // showMore dropdown closing if `closeOnClick` prop is `true`
      if (closeOnClick && !children) {
        dispatch({
          type: ToolbarActionType.CLOSE_SHOW_MORE_DROPDOWN,
          inProgress: true,
        });
      }

      if (children) {
        toggleDropdown(!openDropdown);
      }
    }
  };

  const handleToolbarItemKeyPress: KeyboardEventHandler<HTMLDivElement> = (
    e
  ): void => {
    if (e.key === "Enter" || e.key === " ") {
      onClickHandler(e);
    }
  };

  const renderNodeIcon = (icon?: ReactNode): ReactElement | null => {
    if (Array.isArray(icon)) {
      return (
        <span className="spark-icon spark-toolbar__item-helper spark-toolbar__custom-svg-icon">
          {openDropdown ? icon[1] : icon[0]}
        </span>
      );
    }

    if (!React.isValidElement(icon)) return null;

    const iconNode = icon as ReactElement;
    const childType: any = iconNode.type;

    return childType.displayName === "Icon" ? (
      // Wrapper when Spark <Icon /> provided instead of string as icon name
      React.cloneElement(
        iconNode,
        {
          className: classnames(
            "spark-icon spark-toolbar__item-helper",
            iconNode?.props?.className
          ),
        },
        null
      )
    ) : (
      // Wrapper when custom SVG icon provided
      <span className="spark-icon spark-toolbar__item-helper spark-toolbar__custom-svg-icon">
        {iconNode}
      </span>
    );
  };

  const toolbarItem = (
    <div
      className={classnames(
        "spark-toolbar__item",
        { open: openDropdown },
        { animate: animateDropdown },
        className
      )}
      /**
       * Item's text variant uses css: "content: attr(label)" so we need `label` property
       * regardless of warning "Property 'label' does not exist on type HTMLDivElement"
       */
      // @ts-ignore
      label={label}
      aria-label={ariaLabel || label}
      aria-disabled={disabled}
      role="menuitem"
      tabIndex={-1}
      onKeyUp={!disabled ? handleToolbarItemKeyPress : undefined}
      onClick={!disabled ? onClickHandler : undefined}
      ref={toolbarItemRef}
      {...rest}
    >
      {children}
      {typeof icon === "string" ? (
        <div
          className={classnames(
            renderIconClassName(icon),
            "spark-toolbar__item-helper",
            { "spark-icon--fill": iconStyle === IconStyle.FILL }
          )}
        />
      ) : (
        renderNodeIcon(icon)
      )}
    </div>
  );

  return tooltip && !textButton && visible ? (
    <Tooltip toggleEl={toolbarItem}>{label}</Tooltip>
  ) : (
    toolbarItem
  );
};

ToolbarItem.displayName = "ToolbarItem";
